// SmsPhrase.js
import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import AddIcon from '@mui/icons-material/Add';
import AddCommentIcon from '@mui/icons-material/AddComment';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import {useMutation, useLazyQuery, useReactiveVar} from '@apollo/client';
import { AppObject, AppPalette, AppWord, TableColumnType, ViewMode } from '../../model/AppConst';
import SmsPhraseInput from './SmsPhraseInput';
import {
    GET_SMS_PHRASE_LIST, ADD_SMS_PHRASE, EDIT_SMS_PHRASE, REMOVE_SMS_PHRASE
} from './SmsPhraseGql';
import { Box, Button } from '@mui/material';
import { smsPhraseListRepo } from '../../model/CvoModel';
import ResponseAlert from '../message/ResponseAlert';
import FlexyTable from '../common/flexytable/FlexyTable';
import ConfirmDialog from '../message/ConfirmDialog';
import { ContentHeader, HeaderLeft, HeaderRight } from '../common/CvoStyled';
import SearchBox from '../common/SearchBox';
import { useSnackbar } from 'notistack';

const SmsPhraseContainer = styled(Box)({flexGrow: 1, display:'flex', flexDirection:'column'});

const TableColumns = [
    {id: 'phrase', label: '자주 쓰는 문구', width: 120, show: true, option:{align: 'inherit', type: TableColumnType.TEXT}}
]

export default function SmsPhrase({
    onRequestUsePhrase
}) {
    const [signal, setSignal] = useState({mode:ViewMode.LIST});
    const [search, setSearch] = useState('');
    const smsPhraseList = useReactiveVar(smsPhraseListRepo);
    const [responseAlert, setResponseAlert] = useState(null);
    const [promptToConfirm, setPromptToConfirm] = useState(null);
    const { enqueueSnackbar } = useSnackbar();
    const loadedRef = useRef(0);

    // ##### Call GraphQL #####
    const [addSmsPhrase, responseAdd] = useMutation( ADD_SMS_PHRASE, {
            onCompleted: (data, option) => onCompleteAdd(data, option),
            onError: (error) => {setResponseAlert({open:true, error: error, title: "자주 쓰는 문구 추가 오류"})}
        }
    );
    const [editSmsPhrase, responseEdit] = useMutation( EDIT_SMS_PHRASE, {
            onCompleted: (data, option) => onCompleteEdit(data, option),
            onError: (error) => {setResponseAlert({open:true, error: error, title: "자주 쓰는 문구 수정 오류"})}
        }
    );
    const [removeSmsPhrase, responseRemove] = useMutation(
        REMOVE_SMS_PHRASE, {
            onCompleted: (data, option) => onCompleteRemove(data, option),
            onError: (error) => {setResponseAlert({open:true, error: error, title: "자주 쓰는 문구 삭제 오류"})}
        }
    );

    // ##### Call GraphQL to get List #####
    const [getSmsPhraseList, responseList] = useLazyQuery(GET_SMS_PHRASE_LIST, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
        onError: (error) => {setResponseAlert({open: true, error: error, title: 'SMS 전송기록 조회 오류'})}
    });

    useEffect(()=>{
        getSmsPhraseList();
    }, []);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetList = (data, clientOption) => {
        if(data.smsPhraseList) {
            loadedRef.current = (new Date()).getTime();
            smsPhraseListRepo(data.smsPhraseList);
        }
    };

    const onCompleteAdd = (data, clientOption) => {
        if(data.smsPhraseAdd.ok) {
            getSmsPhraseList();
            setSignal({mode:ViewMode.LIST, item:null});
        }
        else setResponseAlert({open:true, resultData: data.smsPhraseAdd, title: "자주 쓰는 문구 추가 오류"});
    };

    const onCompleteEdit = (data, clientOption) => {
        if(data.smsPhraseEdit.ok) {
            getSmsPhraseList();
            setSignal({mode:ViewMode.LIST, item:null});
        }
        else setResponseAlert({open:true, resultData: data.smsPhraseEdit, title: "자주 쓰는 문구 수정 오류"});
    };

    const onCompleteRemove = (data, clientOption) => {
        if(data.smsPhraseRemove.ok) getSmsPhraseList();
        else setResponseAlert({open:true, resultData: data.smsPhraseRemove, title: "자주 쓰는 문구 삭제 오류"});
    };

    const onChangeSearch = (text) => {
        setSearch(text);
    };

    const onBackToList = () => {
        setSignal({mode:ViewMode.LIST, item:null});
    };

    const onClickSubmitAdd = (data) => {
        addSmsPhrase({variables: {smsPhrase:data}});
    };

    const onClickSubmitEdit = (data) => {
        editSmsPhrase({variables: {smsPhrase:data}});
    };

    const onRequestReload = () => {
        const diff = Date.now() - loadedRef.current;
        if(diff > 5000) {
            getSmsPhraseList();
        }
        else {
            enqueueSnackbar(AppWord.NO_RELOAD_IN_5_SEC,
                {variant:AppPalette.WarnColor, autoHideDuration:1500});
        }
    };

    const setSmsPhrase = (data) => {
        if(onRequestUsePhrase) onRequestUsePhrase(data);
    };

    const onCloseConfirmDialog = (data) => {
        setPromptToConfirm(null);
        // setWork({job:null, item:null});
        if(data) {
            const phraseId = data.phraseId;
            removeSmsPhrase({variables: {smsPhrase:{phraseId:phraseId}}});
        }
    };

    const onClickRemoveItem = (item) => {
        // setWork({job:LocalJob.DELETE, item:item});
        setPromptToConfirm({
            title:"선택한 문구를 삭제하시겠습니까?",
            message:item.phrase,
            data: item
        });
    };

    const onClickToolOnRow = (toolId, row) => {
        switch(toolId) {
            case AppWord.CMD_ADD: setSmsPhrase(row); break;
            case AppWord.CMD_EDIT: setSignal({mode:ViewMode.EDIT, item:row}); break;
            case AppWord.CMD_REMOVE: onClickRemoveItem(row); break;
            default: return;
        }
    };

    const renderPromptToConfirmBox = () => {
        const prompt = promptToConfirm ? promptToConfirm : {};
        return (
            <ConfirmDialog
                open={Boolean(promptToConfirm)}
                prompt={prompt}
                onClose={onCloseConfirmDialog}
            />
        );
    };

    return (
        <SmsPhraseContainer>
            <ContentHeader>
                {
                    signal.mode===ViewMode.EDIT
                    ?
                    <HeaderLeft>
                        <SmsPhraseInput
                            open={signal.mode===ViewMode.EDIT}
                            item={signal.item}
                            onClose={onBackToList}
                            isEdit={true}
                            onClickMutate={onClickSubmitEdit}
                            responseSaving={responseEdit}
                        />
                    </HeaderLeft>
                    :
                    signal.mode===ViewMode.NEW
                    ?
                    <HeaderLeft>
                        <SmsPhraseInput
                            open={signal.mode===ViewMode.NEW}
                            onClose={onBackToList}
                            isEdit={false}
                            onClickMutate={onClickSubmitAdd}
                            responseSaving={responseAdd}
                        />
                    </HeaderLeft>
                    :
                    <>
                    <HeaderLeft>
                        <SearchBox onChange={onChangeSearch} value={search}/>
                    </HeaderLeft>
                    <HeaderRight>
                        <Button
                            variant='contained' onClick={() => setSignal({mode:ViewMode.NEW})}
                            startIcon={<AddIcon fontSize='small' />}
                        >
                            문구 추가
                        </Button>
                    </HeaderRight>
                    </>
                }
            </ContentHeader>
            <FlexyTable
                name="sms_phrase_list"
                uniqueKey="phraseId"
                columns={TableColumns}
                records={smsPhraseList}
                search={search}
                tools={[
                    {id:AppWord.CMD_ADD, icon:<AddCommentIcon fontSize='small' />},
                    {id:AppWord.CMD_EDIT, icon:<EditIcon fontSize='small' />},
                    {id:AppWord.CMD_REMOVE, icon: <DeleteForeverIcon fontSize='small' />}
                ]}
                onClickTool={onClickToolOnRow}
                onRequestReload={onRequestReload}
                onClickOneRow={setSmsPhrase}
            />
            {renderPromptToConfirmBox()}
            <ResponseAlert open={responseAlert ? responseAlert.open : false}
                alertData={responseAlert}
                onClose={() => {setResponseAlert(null)}}/>
        </SmsPhraseContainer>
    );
}
